/** @file bookings/recurring.js — recurring pattern CRUD + attachments. */
import { supabase } from "@/lib/supabaseClient";
import { nullEmpty } from "@/lib/constants";
import { BACKEND, getClientSlug } from "../_shared";

export async function listRecurringPatterns({ siteId = null, clientId = null } = {}) {
  let q = supabase
    .from("lbs_recurring_patterns")
    .select(
      `id, client_id, site_id, bay_id, owner_user_id,
       pattern_type, day_of_week, time_start, time_end,
       start_date, end_date, booking_template,
       requires_approval, is_active, created_at,
       site:lbs_sites!lbs_recurring_patterns_site_id_fkey(name,code),
       bay:lbs_bays!lbs_recurring_patterns_bay_id_fkey(name,code,color)`
    )
    .order("created_at", { ascending: false });
  if (siteId)   q = q.eq("site_id",   siteId);
  if (clientId) q = q.eq("client_id", clientId);
  return q;
}

export async function getRecurringPattern(id) {
  return supabase
    .from("lbs_recurring_patterns")
    .select("*")
    .eq("id", id)
    .maybeSingle();
}

async function buildPatternPayload(v) {
  let client_id = v.client_id;
  if (!client_id && v.site_id) {
    const { data } = await supabase
      .from("lbs_sites")
      .select("client_id")
      .eq("id", v.site_id)
      .maybeSingle();
    client_id = data?.client_id ?? null;
  }
  return nullEmpty({
    client_id,
    site_id: v.site_id,
    bay_id: v.bay_id,
    pattern_type: v.pattern_type,
    day_of_week: (v.day_of_week || []).map((n) => parseInt(n, 10)),
    time_start: v.time_start,
    time_end: v.time_end,
    start_date: v.start_date,
    end_date: v.end_date || null,
    booking_template: v.booking_template || {},
    requires_approval: !!v.requires_approval,
    is_active: v.is_active !== false,
  });
}

export async function createRecurringPattern(values, currentUserId) {
  const payload = await buildPatternPayload(values);
  payload.owner_user_id = currentUserId;
  return supabase
    .from("lbs_recurring_patterns")
    .insert(payload)
    .select()
    .maybeSingle();
}

export async function updateRecurringPattern(id, values) {
  const payload = await buildPatternPayload(values);
  return supabase
    .from("lbs_recurring_patterns")
    .update(payload)
    .eq("id", id)
    .select()
    .maybeSingle();
}

export async function deleteRecurringPattern(id) {
  return supabase.from("lbs_recurring_patterns").delete().eq("id", id).select();
}

export async function togglePatternActive(id, isActive) {
  return supabase
    .from("lbs_recurring_patterns")
    .update({ is_active: isActive })
    .eq("id", id)
    .select()
    .maybeSingle();
}

export async function generateRecurringNow({ horizonDays = 14, patternId = null } = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${BACKEND}/api/recurring/generate-now`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: session?.access_token ? `Bearer ${session.access_token}` : "",
    },
    body: JSON.stringify({ horizon_days: horizonDays, pattern_id: patternId }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let msg = body;
    try {
      const j = JSON.parse(body);
      msg = j?.detail || j?.message || body;
    } catch (_e) { /* raw */ }
    return { error: { message: msg || `HTTP ${res.status}` } };
  }
  return { data: await res.json() };
}

export async function listPatternsForGrid(siteId, startISO, endISO) {
  const startDate = startISO.slice(0, 10);
  const endDate = endISO.slice(0, 10);
  return supabase
    .from("lbs_recurring_patterns")
    .select(
      `id, client_id, site_id, bay_id, pattern_type, day_of_week,
       time_start, time_end, start_date, end_date, booking_template,
       requires_approval, is_active`
    )
    .eq("site_id", siteId)
    .lte("start_date", endDate)
    .or(`end_date.is.null,end_date.gte.${startDate}`);
}

export async function listPatternAttachments(patternId) {
  return supabase
    .from("lbs_booking_attachments")
    .select("id, attachment_type, file_name, file_path, file_size, mime_type, created_at")
    .eq("pattern_id", patternId)
    .order("created_at", { ascending: false });
}

export async function uploadPatternAttachment({ pattern, file, attachmentType }) {
  const slug = await getClientSlug(pattern.client_id);
  if (!slug) return { error: { message: "Client storage bucket not provisioned." } };
  const bucket = `lbs-${slug}`;
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
  const path = `patterns/${pattern.id}/${Date.now()}-${safeName}`;

  // Same workaround as uploadSiteRuleFile — read into ArrayBuffer first
  // to dodge the supabase-js storage "Body has already been consumed" bug.
  let buffer;
  try {
    buffer = await file.arrayBuffer();
  } catch (e) {
    return { error: { message: e?.message || "Could not read file" } };
  }
  try {
    const up = await supabase.storage.from(bucket).upload(path, buffer, {
      contentType: file.type || "application/octet-stream",
      upsert: true,
    });
    if (up.error) return { error: { message: up.error.message || "Upload failed" } };
  } catch (e) {
    return { error: { message: e?.message || "Upload failed" } };
  }
  return supabase
    .from("lbs_booking_attachments")
    .insert({
      pattern_id: pattern.id,
      attachment_type: attachmentType,
      file_name: file.name,
      file_path: `${bucket}/${path}`,
      file_size: file.size,
      mime_type: file.type || null,
    })
    .select()
    .maybeSingle();
}

export async function deletePatternAttachment(row) {
  if (row?.file_path) {
    const [bucket, ...rest] = row.file_path.split("/");
    const key = rest.join("/");
    if (bucket && key) await supabase.storage.from(bucket).remove([key]);
  }
  return supabase.from("lbs_booking_attachments").delete().eq("id", row.id).select();
}

/* =====================  BLACKOUTS  ============================ */
